import { WebsiteBuilderData } from "api/websiteBuilderApi";
import WebsiteBuilderPhonePreview from "components/websiteBuilder/WebsiteBuilderPhonePreview";
import CardSymphony from "elements/CardSymphony";
import Icon from "elements/Icon";
import Loader from "elements/Loader";
import PageSymphony from "elements/PageSymphony";
import SliderSymphony from "elements/SliderSymphony";
import SwitchSymphony from "elements/SwitchSymphony";
import useScreen from "Hooks/useScreen";
import useWebsiteBuilder from "Hooks/useWebsiteBuilder";
import { makeKey, styx } from "modules/Utils";
import { CSSProperties, useEffect, useState } from "react";
import dayjs from 'dayjs';
import Sty from "modules/Sty";
import { toast } from "react-toastify";

const AllWebsites = () => {
    const {
        mStyle
    } = useScreen();
    const {
        getWebsites,
        websites,
        isLoadingWebsiteBuilder,
    } = useWebsiteBuilder();

    const [isRow, setIsRow] = useState<boolean>(true);
    const [isShowInfo, setIsShowInfo] = useState<boolean>(true);
    const [isShowPreview, setIsShowPreview] = useState<boolean>(true);
    const [zoomLevel, setZoomLevel] = useState<number>(75);

    useEffect(() => {
        getWebsites({ isAllWebsites: true, includeBrand: true });
    }, [])

    /** Zoom level slider marks */
    const zoomMarks = [
        { label: 'Small', description: '50%', value: 50, placement: 'first' as const },
        { label: 'Medium', description: '75%', value: 75, placement: 'middle' as const },
        { label: 'Large', description: '100%', value: 100, placement: 'last' as const },
    ];

    /** Format date using dayjs with a readable format */
    const formatDate = (date: string | Date | null | undefined) => {
        if (!date) {
            return 'N/A'
        }
        return dayjs(date).format('MM/DD/YYYY');
    };

    const newTab = (url: string) => {
        window.open(url, '_blank');
    }

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    }

    const infoText = (title: string, value?: string | number | null | undefined) => {
        return (
            <>
                <span style={Sty.text14bold}>{title}:</span> {value}
            </>
        )
    }

    const renderInfo = (website: WebsiteBuilderData) => {
        if (!isShowInfo) {
            return null;
        }

        const {
            brand,
            createdAt,
            updatedAt,
        } = website;

        const devLink = `http://localhost:3001/${brand?.slug}/${website?.slug}`;
        const prodLink = `https://symphony.to/${brand?.slug}/${website?.slug}`;

        return (
            <div className="allWebsitesWebsitePreviewBrand" style={sty.allWebsitesWebsitePreviewBrand}>
                <div className="websiteInfoBrandName">
                    {infoText("Brand", brand?.name)}
                </div>
                <div className="websiteInfoBrandId">
                    {infoText("Brand ID", brand?.id)}
                </div>
                <div className="websiteInfoWebsiteId">
                    {infoText("Website ID", website?.id)}
                </div>
                <div className="websiteInfoBrandSlug" style={sty.websiteInfoBrandSlug}>
                    {infoText("Brand Slug", brand?.slug)}
                </div>
                <div className="websiteInfoWebsiteSlug" style={sty.websiteInfoWebsiteSlug}>
                    {infoText("Website Slug", website?.slug)}
                </div>

                <div className="websiteInfoCreatedDate">
                    {infoText("Created", formatDate(createdAt))}
                </div>
                <div className="websiteInfoUpdatedDate">
                    {infoText("Updated", formatDate(updatedAt))}
                </div>

                <div className="websiteInfoLinks" style={sty.websiteInfoLinks}>
                    <Icon
                        className="allWebsitesWebsitePreviewBrandLink"
                        icon="copy"
                        onClick={() => copyToClipboard(devLink)}
                        color="purple"
                    />
                    <Icon
                        className="allWebsitesWebsitePreviewBrandLink"
                        icon="copy"
                        onClick={() => copyToClipboard(prodLink)}
                        color="green"
                    />
                    <Icon
                        className="allWebsitesWebsitePreviewBrandLink"
                        icon="externalLink"
                        onClick={() => newTab(devLink)}
                    />
                    <Icon
                        className="allWebsitesWebsitePreviewBrandLink"
                        icon="externalLink"
                        onClick={() => newTab(prodLink)}
                        color="green"
                    />
                </div>
            </div>
        )
    }

    return (
        <PageSymphony
            className="allWebsitesPage"
            isContent
        >
            <CardSymphony
                className="allWebsitesCard"
                title="All Websites"
                subtitle="View all websites"
                isLoading={isLoadingWebsiteBuilder}
            >
                <div className="allWebsitesControls" style={mStyle(sty, 'allWebsitesControls')}>
                    <div>
                        <SwitchSymphony
                            className="allWebsitesSwitch"
                            label={isRow ? "Columns" : "Rows"}
                            checked={isRow}
                            onChange={() => setIsRow(!isRow)}
                        />
                        <SwitchSymphony
                            className="allWebsitesSwitch"
                            label="Info"
                            checked={isShowInfo}
                            onChange={() => setIsShowInfo(!isShowInfo)}
                        />
                        <SwitchSymphony
                            className="allWebsitesSwitch"
                            label="Preview"
                            checked={isShowPreview}
                            onChange={() => setIsShowPreview(!isShowPreview)}
                        />
                    </div>
                    <SliderSymphony
                        className="allWebsitesZoomSlider"
                        style={{ maxWidth: 320 }}
                        value={zoomLevel}
                        sliderOffset={3}
                        min={50}
                        max={100}
                        marks={zoomMarks}
                        onChange={(value) => setZoomLevel(value as number)}
                    />
                </div>
                <div
                    className="allWebsitesPhonePreviews"
                    style={styx(
                        sty.allWebsitesPhonePreviews,
                        isRow ? {
                            flexDirection: 'row',
                        } : {
                            flexDirection: 'column',
                        },
                    )}
                >
                    {websites.map((website, index) => {
                        const {
                            websiteContent,
                            websiteStyle,
                        } = website;

                        return (
                            <div
                                className="allWebsitesWebsitePreview"
                                style={styx(
                                    sty.allWebsitesWebsitePreview,
                                    isRow ? {
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                    } : {
                                        flexDirection: 'row',
                                    },
                                )}
                            >
                                {renderInfo(website)}
                                {isShowPreview &&
                                    <WebsiteBuilderPhonePreview
                                        key={makeKey('websiteTemplate', index)}
                                        className="websiteTemplatePreviewPhonePreviewFromCreateNewWebsiteModalV2"
                                        style={{ zoom: zoomLevel / 100 }}
                                        websiteContent={websiteContent}
                                        websiteStyle={websiteStyle}
                                        isSticky={false}
                                    />
                                }
                            </div>
                        )
                    })}
                </div>
            </CardSymphony>
        </PageSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    allWebsitesControls: {
        display: 'flex',
        flexDirection: 'row',
        gap: 24,
        width: '100%',
        justifyContent: 'space-between',
        marginBottom: 24,
    },
    allWebsitesControls_mobile: {
        flexDirection: 'column',
        gap: 16,
    },
    allWebsitesZoomControl: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    allWebsitesZoomLabel: {
        marginBottom: 8,
    },
    allWebsitesPhonePreviews: {
        display: 'flex',
        width: '100%',
        gap: 16,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    allWebsitesWebsitePreview: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    allWebsitesWebsitePreviewBrand: {
        ...Sty.text14grey,
        marginRight: 'auto',
    },

    websiteInfoBrandSlug: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%'
    },
    websiteInfoWebsiteSlug: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%'
    },
    websiteInfoLinks: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
    }
}

export default AllWebsites;